import { ActivityRepository, RecentPage } from './types';

const MAX_SERIALIZED_BYTES = 50 * 1024; // 50 KB

export class LocalStorageActivityRepository implements ActivityRepository {
  private memCache = new Map<string, RecentPage[]>();

  private getKey(scope: string): string {
    return `helios.recent.v1:${scope || 'anon'}`;
  }

  load(scope: string): RecentPage[] {
    const key = this.getKey(scope);
    if (typeof window === 'undefined' || !window.localStorage) {
      return this.memCache.get(key) || [];
    }

    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return this.memCache.get(key) || [];
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.memCache.set(key, parsed);
        return parsed;
      }
    } catch {
      // Corrupt JSON or storage error — fallback to memory
    }
    return this.memCache.get(key) || [];
  }

  save(scope: string, pages: RecentPage[]): void {
    const key = this.getKey(scope);
    this.memCache.set(key, pages);

    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      let payload = JSON.stringify(pages);
      let truncated = [...pages];

      // If serialized size exceeds cap, prune oldest pages until within budget
      while (payload.length > MAX_SERIALIZED_BYTES && truncated.length > 1) {
        truncated.pop();
        payload = JSON.stringify(truncated);
      }

      localStorage.setItem(key, payload);
    } catch {
      // Storage quota exceeded or disabled
    }
  }

  clear(scope: string): void {
    const key = this.getKey(scope);
    this.memCache.delete(key);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
  }
}

export const defaultActivityRepository = new LocalStorageActivityRepository();
